import React, { useState } from 'react';
import { Option, BaseInputLayoutProps, BaseInputProps, InputLayoutIcon } from './types';
import { InputHelper, useInputHelper } from './inputHelper';
import { useNewUiTheme } from '../../newUiTheme';
import { useFormContext } from '../FormContext';

export type DropdownTypes = string | number | boolean | null;

export type OptionsGroup = {
  label: string,
  options: Option[],
}

interface DropdownHelper extends InputHelper<DropdownTypes> {

}

function useDropdownHelper<ValueType extends DropdownTypes = number>(
  props: DropdownProps<ValueType>
): DropdownHelper {
  return useInputHelper<ValueType>(props);
}

export interface DropdownProps<
  ValueType extends DropdownTypes = number
> extends BaseInputProps<ValueType> {
  options: Option<ValueType>[] | OptionsGroup[];
  onInputChange?: (value: string) => void;
  isClearable?: boolean;
  icon?: InputLayoutIcon;
  optionCreateFunc?: (value: string) => Promise<Option<ValueType>>,
  groupFunc?: (option: Option<ValueType>) => string,
  layoutTheme?: DropdownLayoutTheme,
  mobile_hideIcon?: boolean;
  mobile_noValueOption?: string;
}

export enum DropdownLayoutTheme {
  STANDARD = 'standard',
  COMPACT = 'compact',
}

export interface DropdownLayoutProps extends BaseInputLayoutProps<DropdownTypes> {
  options?: Option[] | OptionsGroup[],
  isClearable?: boolean
  icon?: InputLayoutIcon;
  onInputChange?: (value: string) => void
  optionCreateFunc?: (value: string) => Promise<Option<DropdownTypes>>,
  theme?: string
  mobile_hideIcon?: boolean
  mobile_noValueOption?: string
}

const areOptionsGrouped = (options: Option[] | OptionsGroup[]) => {
  return options && options?.length > 0 && Array.isArray(options[0].options);
};

export function Dropdown<ValueType extends DropdownTypes = number>(props: DropdownProps<ValueType>) {
  const helper = useDropdownHelper<ValueType>(props);
  const uiTheme = useNewUiTheme();

  const [newOptions, setNewOptions] = useState<Option[]>([]);

  let options = areOptionsGrouped(props.options) ? props.options : [
    ...props.options,
    ...newOptions
  ] as Option[];

  const formContext = useFormContext();

  const optionCreateFunc = props.optionCreateFunc ? async (value: string) => {
    const newOption = await props.optionCreateFunc!(value);

    setNewOptions([
      ...newOptions,
      newOption
    ]);

    formContext?.onChangeCallback?.({
      [props.source]: newOption.value
    });

    return newOption;
  } : undefined;

  if (props.groupFunc) {
    const groupedOptionsMap: Record<string, Option[]> = {};
    (options as Option<ValueType>[]).forEach((option) => {
      const group = props.groupFunc!(option);
      if (!groupedOptionsMap[group]) {
        groupedOptionsMap[group] = [];
      }
      groupedOptionsMap[group].push(option);
    });

    options = Object.keys(groupedOptionsMap).map((group) => ({
      label: group,
      options: groupedOptionsMap[group],
    })) as OptionsGroup[];
  }

  return (
    <uiTheme.inputs.BaseDropdownLayout
      {...helper.getBaseInputLayoutProps()}
      options={options}
      isClearable={props.isClearable}
      hideLabel={props.hideLabel}
      icon={props.icon}
      onInputChange={props.onInputChange}
      optionCreateFunc={optionCreateFunc}
      mobile_noValueOption={props.mobile_noValueOption}
      theme={props.layoutTheme}
    />
  );
}
